import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ArrowLeft,
  Download,
  ImagePlus,
  LayoutGrid,
  MousePointer2,
  MoveUpRight,
  Palette,
  Pencil,
  Redo2,
  Share2,
  Square,
  Type,
  Undo2,
} from "lucide-react";
import client from "../api/client";
import AssetPanel from "./AssetPanel";
import BoardCanvas from "./BoardCanvas";
import CropDialog from "./CropDialog";
import { alignItems, autoArrange, distributeItems } from "./geometry";
import Inspector from "./Inspector";
import Modal from "./Modal";
import PageStrip, { MAX_PAGES } from "./PageStrip";
import TemplatePicker from "./TemplatePicker";
import DrawOptions from "./DrawOptions";
import { TOOL_DEFAULTS, applyTemplate, aspectRatio, makeImage, makeSwatch, makeText, uid } from "./templates";
import useUploads from "./useUploads";
import "./Moodboard.css";

const HISTORY_LIMIT = 80;
// Must match MAX_ITEMS in api/moodboards.py.
const MAX_ITEMS = 80;
const MERGE_WINDOW_MS = 1000;
const AUTOSAVE_DELAY_MS = 1000;
const PASTE_OFFSET = 2;

const STATUS_LABELS = {
  saved: "All changes saved",
  unsaved: "Unsaved changes",
  saving: "Saving...",
  error: "Could not save",
  conflict: "Changed elsewhere",
};

// Survives navigating between boards, so items can be copied from one to another.
let clipboard = null;

const TOOLS = [
  { key: "select", icon: MousePointer2, label: "Select", shortcut: "v" },
  { key: "pencil", icon: Pencil, label: "Pencil", shortcut: "p" },
  { key: "shape", icon: Square, label: "Shape", shortcut: "b" },
  { key: "arrow", icon: MoveUpRight, label: "Arrow", shortcut: "a" },
];
const DRAW_KIND = { draw: "pencil", shape: "shape", arrow: "arrow" };
const STYLE_KEYS = ["brush", "shape", "route", "bend", "head_start", "head_end", "stroke", "stroke_width", "fill", "radius", "dash"];

// The board on screen is doc.items and doc.background; doc.pages holds every
// board, and the one on screen is copied back into it on each change.
const syncPage = (doc) => ({
  ...doc,
  pages: doc.pages.map((page, index) => (index === doc.page ? { ...page, background: doc.background, items: doc.items } : page)),
});
const showPage = (doc, index) => ({ ...doc, page: index, background: doc.pages[index].background, items: doc.pages[index].items });
// What is saved: the page on screen is not part of the board. Board 1 also goes
// in the single-board fields, so a server without pages still keeps it.
const persisted = (doc) => ({
  title: doc.title,
  aspect: doc.aspect,
  pages: doc.pages,
  background: doc.pages[0].background,
  items: doc.pages[0].items,
});
const snapshot = (doc) => {
  const { aspect, background, items, pages, page } = syncPage(doc);
  return { aspect, background, items, pages, page };
};
const clamp = (value, low, high) => Math.min(high, Math.max(low, value));
const nextZ = (items) => items.reduce((max, item) => Math.max(max, item.z), 0) + 1;

const centred = (w, h, point) => ({
  x: clamp((point?.x ?? 50) - w / 2, 0, 100 - w),
  y: clamp((point?.y ?? 50) - h / 2, 0, 100 - h),
  w,
  h,
});

const isTyping = (target) =>
  target instanceof HTMLElement &&
  (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));

function fileNameFrom(disposition, fallback) {
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition || "");
  return match ? decodeURIComponent(match[1]) : fallback;
}

function ShareDialog({ boardId, token, onToken, onClose }) {
  const [busy, setBusy] = useState(false);
  const link = token ? `${window.location.origin}/shared/moodboards/${token}` : "";

  const request = async (method) => {
    setBusy(true);
    try {
      const { data } = await client.request({ method, url: `/api/moodboards/${boardId}/share` });
      onToken(data.share_token);
    } catch {
      toast.error("Could not update the share link.");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copied.");
    } catch {
      toast.info("Select the link and copy it manually.");
    }
  };

  return (
    <Modal title="Share board" onClose={onClose}>
      {token ? (
        <>
          <p className="mb-modal-text">
            Anyone with this link can view the board and download it as a branded PDF or PNG. They cannot edit it, and
            changes you make appear on the link straight away.
          </p>
          <div className="mb-share-link">
            <input readOnly value={link} onFocus={(event) => event.target.select()} aria-label="Share link" />
            <button type="button" className="mb-primary" onClick={copy}>
              Copy
            </button>
          </div>
          <button type="button" className="mb-text-button danger" onClick={() => request("delete")} disabled={busy}>
            Turn off link
          </button>
        </>
      ) : (
        <>
          <p className="mb-modal-text">
            Create a read-only link to send to a client or colleague. They will not need an account, and you can turn
            the link off at any time.
          </p>
          <button type="button" className="mb-primary" onClick={() => request("post")} disabled={busy}>
            {busy ? "Creating..." : "Create link"}
          </button>
        </>
      )}
    </Modal>
  );
}

function LayoutDialog({ doc, onApply, onClose }) {
  const [template, setTemplate] = useState(null);
  const plan = useMemo(
    () => (template ? applyTemplate(template, doc.aspect, doc.items) : null),
    [template, doc.aspect, doc.items]
  );
  const replacesOther = doc.items.some((item) => item.type !== "image");

  return (
    <Modal title="Change layout" onClose={onClose} wide>
      <p className="mb-modal-text">
        Your images are kept and placed into the new layout in order. Text, swatches and drawings are replaced by the
        layout's own.
        To keep your layout and just fit more images in, use Auto-arrange instead.
      </p>
      <TemplatePicker aspect={doc.aspect} value={template} onChange={setTemplate} />
      {plan && (plan.dropped > 0 || replacesOther) && (
        <p className="mb-warning">
          {plan.dropped > 0 && `${plan.dropped} image${plan.dropped === 1 ? "" : "s"} will not fit and will be removed. `}
          {replacesOther && "Existing text, swatches and drawings will be removed. "}
          You can undo this.
        </p>
      )}
      <div className="mb-modal-actions">
        <button type="button" className="mb-text-button" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="mb-primary" disabled={!plan} onClick={() => onApply(plan.items)}>
          Apply layout
        </button>
      </div>
    </Modal>
  );
}

export default function MoodboardEditor() {
  const { boardId } = useParams();
  const [assets, setAssets] = useState({});
  const uploads = useUploads({
    // A deleted upload should show as an empty frame, not a broken image.
    onRemoved: (id) =>
      setAssets((prev) => {
        const next = { ...prev };
        delete next[`upload:${id}`];
        return next;
      }),
  });

  const [doc, setDoc] = useState(null);
  const [shareToken, setShareToken] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [status, setStatusState] = useState("saved");
  const [dialog, setDialog] = useState(null);
  const [exporting, setExporting] = useState(null);
  const [historyFlags, setHistoryFlags] = useState({ undo: false, redo: false });
  const [palette, setPalette] = useState([]);
  const [paletteBusy, setPaletteBusy] = useState(false);
  const [removingItem, setRemovingItem] = useState(null);
  const [cropId, setCropId] = useState(null);
  const [tool, setTool] = useState("select");
  // Each drawing tool remembers its last settings, so the next drawing matches.
  const [toolStyles, setToolStyles] = useState(TOOL_DEFAULTS);

  const docRef = useRef(null);
  const selectedRef = useRef([]);
  selectedRef.current = selectedIds;
  const assetsRef = useRef({});
  assetsRef.current = assets;
  const statusRef = useRef("saved");
  const versionRef = useRef(0);
  const savedRef = useRef("");
  const saveChain = useRef(Promise.resolve());
  const history = useRef({ past: [], future: [], mergeKey: null, mergeAt: 0 });

  const setStatus = useCallback((value) => {
    statusRef.current = value;
    setStatusState(value);
  }, []);

  const syncHistoryFlags = () =>
    setHistoryFlags({ undo: history.current.past.length > 0, redo: history.current.future.length > 0 });

  const hydrate = useCallback(
    (data) => {
      const pages = data.pages?.length ? data.pages : [{ id: uid(), background: data.background, items: data.items || [] }];
      const next = showPage({ title: data.title, aspect: data.aspect, pages, page: 0 }, 0);
      docRef.current = next;
      versionRef.current = data.version;
      savedRef.current = JSON.stringify(persisted(next));
      history.current = { past: [], future: [], mergeKey: null, mergeAt: 0 };
      setDoc(next);
      setAssets(data.assets || {});
      setShareToken(data.share_token || null);
      setHistoryFlags({ undo: false, redo: false });
      setStatus("saved");
    },
    [setStatus]
  );

  const load = useCallback(async () => {
    try {
      const { data } = await client.get(`/api/moodboards/${boardId}`);
      hydrate(data);
      setLoadError("");
    } catch (err) {
      setLoadError(err.response?.status === 404 ? "This board does not exist or has been deleted." : "Could not load the board.");
    }
  }, [boardId, hydrate]);

  useEffect(() => {
    load();
  }, [load]);

  // --- saving ---------------------------------------------------------------

  const doSave = useCallback(async () => {
    const current = docRef.current;
    if (!current || statusRef.current === "conflict") return;
    const payload = JSON.stringify(persisted(current));
    if (payload === savedRef.current) {
      if (statusRef.current !== "saved") setStatus("saved");
      return;
    }

    setStatus("saving");
    try {
      const { data } = await client.put(`/api/moodboards/${boardId}`, {
        ...persisted(current),
        title: current.title.trim() || "Untitled board",
        version: versionRef.current,
      });
      versionRef.current = data.version;
      savedRef.current = payload;
      // Keep URLs already on screen (uploads come back freshly signed and
      // swapping them would reload every image), except provisional library
      // thumbnails, which are upgraded to the sharp transparent preview.
      setAssets((prev) => {
        const next = { ...data.assets };
        Object.entries(prev).forEach(([key, asset]) => {
          if (!asset.provisional || !next[key]) next[key] = asset;
        });
        return next;
      });
      setStatus(JSON.stringify(persisted(docRef.current)) === payload ? "saved" : "unsaved");
    } catch (err) {
      if (err.response?.status === 409) {
        setStatus("conflict");
        return;
      }
      setStatus("error");
      toast.error(err.response?.data?.error || "Could not save the board. Check your connection.", {
        toastId: "save-error",
      });
    }
  }, [boardId, setStatus]);

  // Saves run one after another, so a slow request can never land after a newer one.
  const save = useCallback(() => {
    saveChain.current = saveChain.current.then(doSave);
    return saveChain.current;
  }, [doSave]);

  useEffect(() => {
    if (!doc || statusRef.current === "conflict") return undefined;
    if (JSON.stringify(persisted(doc)) === savedRef.current) {
      // Edits that ended back where the last save was leave nothing to save.
      if (statusRef.current === "unsaved") setStatus("saved");
      return undefined;
    }
    if (statusRef.current !== "saving") setStatus("unsaved");
    const timer = setTimeout(save, AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [doc, save, setStatus]);

  useEffect(() => {
    const warn = (event) => {
      if (docRef.current && JSON.stringify(persisted(docRef.current)) !== savedRef.current) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => {
      window.removeEventListener("beforeunload", warn);
      // Leaving the page inside the app: flush whatever the debounce was holding.
      save();
    };
  }, [save]);

  const resolveConflict = async (keepMine) => {
    if (!keepMine) {
      await load();
      return;
    }
    try {
      const { data } = await client.get(`/api/moodboards/${boardId}`);
      versionRef.current = data.version;
      setStatus("unsaved");
      await save();
    } catch {
      setStatus("error");
    }
  };

  // --- editing --------------------------------------------------------------

  const apply = useCallback((next, { record = true, before, merge } = {}) => {
    const current = docRef.current;
    if (!current || next === current) return;
    if (record) {
      const h = history.current;
      const now = Date.now();
      const merging = merge && h.mergeKey === merge && now - h.mergeAt < MERGE_WINDOW_MS;
      if (!merging) {
        h.past.push(snapshot(before ? { ...current, items: before } : current));
        if (h.past.length > HISTORY_LIMIT) h.past.shift();
      }
      h.future = [];
      h.mergeKey = merge || null;
      h.mergeAt = now;
      syncHistoryFlags();
    }
    const synced = syncPage(next);
    docRef.current = synced;
    setDoc(synced);
  }, []);

  const setItems = useCallback(
    (items, options) => apply({ ...docRef.current, items }, options),
    [apply]
  );

  const updateItem = useCallback(
    (id, patch, options) =>
      setItems(
        docRef.current.items.map((item) => (item.id === id ? { ...item, ...patch } : item)),
        options
      ),
    [setItems]
  );

  const updateItems = useCallback(
    (ids, patch) => setItems(docRef.current.items.map((item) => (ids.includes(item.id) ? { ...item, ...patch } : item))),
    [setItems]
  );

  const updateBoard = useCallback(
    (patch, options) => apply({ ...docRef.current, ...patch }, options),
    [apply]
  );

  const travel = useCallback((from, to) => {
    const h = history.current;
    if (!h[from].length) return;
    const current = docRef.current;
    h[to].push(snapshot(current));
    const next = { ...current, ...h[from].pop() };
    h.mergeKey = null;
    docRef.current = next;
    setDoc(next);
    syncHistoryFlags();
  }, []);

  // --- boards (pages) --------------------------------------------------------

  const goToPage = useCallback((index) => {
    const current = docRef.current;
    if (!current || index === current.page || index < 0 || index >= current.pages.length) return;
    // Moving between boards is not an edit: no undo step and nothing to save.
    const next = showPage(syncPage(current), index);
    docRef.current = next;
    setDoc(next);
    setSelectedIds([]);
  }, []);

  const changePages = useCallback(
    (pages, index) => {
      apply(showPage({ ...syncPage(docRef.current), pages }, index));
      setSelectedIds([]);
    },
    [apply]
  );

  const addPage = useCallback(() => {
    const current = syncPage(docRef.current);
    if (current.pages.length >= MAX_PAGES) return;
    const pages = [...current.pages];
    pages.splice(current.page + 1, 0, { id: uid(), background: current.background, items: [] });
    changePages(pages, current.page + 1);
  }, [changePages]);

  const duplicatePage = useCallback(() => {
    const current = syncPage(docRef.current);
    if (current.pages.length >= MAX_PAGES) return;
    const pages = [...current.pages];
    const source = pages[current.page];
    pages.splice(current.page + 1, 0, { ...source, id: uid(), items: source.items.map((item) => ({ ...item, id: uid() })) });
    changePages(pages, current.page + 1);
  }, [changePages]);

  const deletePage = useCallback(() => {
    const current = syncPage(docRef.current);
    if (current.pages.length === 1) return;
    if (current.items.length && !window.confirm(`Delete board ${current.page + 1} and everything on it? You can undo this.`)) return;
    const pages = current.pages.filter((_, index) => index !== current.page);
    changePages(pages, Math.min(current.page, pages.length - 1));
  }, [changePages]);

  const movePage = useCallback(
    (delta) => {
      const current = syncPage(docRef.current);
      const target = current.page + delta;
      if (target < 0 || target >= current.pages.length) return;
      const pages = [...current.pages];
      [pages[current.page], pages[target]] = [pages[target], pages[current.page]];
      changePages(pages, target);
    },
    [changePages]
  );

  const undo = useCallback(() => travel("past", "future"), [travel]);
  const redo = useCallback(() => travel("future", "past"), [travel]);

  useEffect(() => {
    if (!doc || !selectedIds.length) return;
    const present = selectedIds.filter((id) => doc.items.some((item) => item.id === id));
    if (present.length !== selectedIds.length) setSelectedIds(present);
  }, [doc, selectedIds]);

  const addItems = useCallback(
    (newItems, { select = true } = {}) => {
      const items = docRef.current.items;
      if (items.length + newItems.length > MAX_ITEMS) {
        toast.error(`A board can hold up to ${MAX_ITEMS} items.`, { toastId: "item-limit" });
        return false;
      }
      let z = nextZ(items);
      setItems([...items, ...newItems.map((item) => ({ ...item, z: z++ }))]);
      if (select) setSelectedIds(newItems.map((item) => item.id));
      return true;
    },
    [setItems]
  );

  // Pencil strokes keep the pencil in hand; a finished box or arrow is selected
  // so it can be adjusted straight away.
  const createDrawn = useCallback(
    (item, { keepTool = false } = {}) => {
      addItems([item], { select: !keepTool });
      if (!keepTool) setTool("select");
    },
    [addItems]
  );

  const changeToolStyle = useCallback(
    (kind, patch) => setToolStyles((current) => ({ ...current, [kind]: { ...current[kind], ...patch } })),
    []
  );

  // Restyling a drawing also becomes the style for the next one of its kind.
  const changeFromInspector = useCallback(
    (id, patch, options) => {
      const item = docRef.current.items.find((entry) => entry.id === id);
      const kind = item && DRAW_KIND[item.type];
      if (kind) {
        const style = Object.fromEntries(Object.entries(patch).filter(([key]) => STYLE_KEYS.includes(key)));
        if (Object.keys(style).length) changeToolStyle(kind, style);
      }
      updateItem(id, patch, options);
    },
    [changeToolStyle, updateItem]
  );

  const addItem = useCallback((item) => addItems([item]), [addItems]);

  // targetId: dropped straight onto that frame, which it replaces. Otherwise
  // fill the selected empty frame or the next empty one in reading order, and
  // once every frame is filled add a new image instead of replacing one.
  const placeAsset = useCallback(
    (asset, { targetId, point, fillEmpty = !point } = {}) => {
      const key = `${asset.source}:${asset.ref}`;
      setAssets((prev) =>
        prev[key] && !prev[key].provisional
          ? prev
          : {
              ...prev,
              [key]: {
                url: asset.url,
                width: asset.width,
                height: asset.height,
                name: asset.name,
                transparent: asset.transparent,
                provisional: asset.provisional,
              },
            }
      );

      const fit = asset.transparent ? "contain" : "cover";
      const images = docRef.current.items.filter((item) => item.type === "image" && !item.locked);
      let target = targetId ? images.find((item) => item.id === targetId) : null;
      if (!target && fillEmpty) {
        const chosen = images.find((item) => selectedRef.current.includes(item.id) && !item.source);
        target = chosen || images.filter((item) => !item.source).sort((a, b) => a.y - b.y || a.x - b.x)[0];
      }

      if (target) {
        updateItem(target.id, {
          source: asset.source,
          ref: asset.ref,
          fit,
          focus_x: 50,
          focus_y: 50,
          zoom: 1,
          crop: null,
          flip_x: false,
          flip_y: false,
        });
        setSelectedIds([target.id]);
        return;
      }

      const ratio = aspectRatio(docRef.current.aspect);
      const imageRatio = asset.width && asset.height ? asset.width / asset.height : 1.5;
      let w = 24;
      let h = (w * ratio) / imageRatio;
      if (h > 50) {
        h = 50;
        w = (h * imageRatio) / ratio;
      }
      addItem(makeImage(centred(w, h, point), { source: asset.source, ref: asset.ref, fit }));
    },
    [addItem, updateItem]
  );

  const dropFiles = useCallback(
    async (files, { targetId, point }) => {
      const added = await uploads.upload(files);
      added.forEach((asset, index) => {
        if (index === 0) {
          placeAsset(asset, { targetId, point });
          return;
        }
        // The rest go into empty frames, or are fanned out beside the drop point.
        const offset = point && { x: point.x + index * 3, y: point.y + index * 3 };
        placeAsset(asset, { point: offset, fillEmpty: true });
      });
    },
    [placeAsset, uploads]
  );

  const arrange = useCallback(
    (id, where) => {
      const ordered = [...docRef.current.items].sort((a, b) => a.z - b.z);
      const moving = ordered.find((item) => item.id === id);
      const rest = ordered.filter((item) => item.id !== id);
      const next = where === "front" ? [...rest, moving] : [moving, ...rest];
      setItems(next.map((item, index) => ({ ...item, z: index + 1 })));
    },
    [setItems]
  );

  const cloneItems = useCallback((items) => {
    const ratio = aspectRatio(docRef.current.aspect);
    return items.map((item) => ({
      ...item,
      id: uid(),
      locked: false,
      x: clamp(item.x + PASTE_OFFSET, 0, 100 - item.w),
      y: clamp(item.y + PASTE_OFFSET * ratio, 0, 100 - item.h),
    }));
  }, []);

  const selectedItems = useCallback(
    () => docRef.current.items.filter((item) => selectedRef.current.includes(item.id)),
    []
  );

  const duplicateSelected = useCallback(() => {
    const chosen = selectedItems();
    if (chosen.length) addItems(cloneItems(chosen));
  }, [addItems, cloneItems, selectedItems]);

  const removeSelected = useCallback(() => {
    const ids = selectedItems()
      .filter((item) => !item.locked)
      .map((item) => item.id);
    if (!ids.length) {
      if (selectedRef.current.length) toast.info("Unlock the item first to delete it.", { toastId: "locked" });
      return;
    }
    setItems(docRef.current.items.filter((item) => !ids.includes(item.id)));
  }, [selectedItems, setItems]);

  const copySelected = useCallback(() => {
    const chosen = selectedItems();
    if (!chosen.length) return;
    const assetsNeeded = {};
    chosen.forEach((item) => {
      const key = `${item.source}:${item.ref}`;
      if (assetsRef.current[key]) assetsNeeded[key] = assetsRef.current[key];
    });
    clipboard = { items: chosen, assets: assetsNeeded };
    toast.info(`Copied ${chosen.length} item${chosen.length === 1 ? "" : "s"}.`, { toastId: "copied", autoClose: 1200 });
  }, [selectedItems]);

  const paste = useCallback(() => {
    if (!clipboard?.items.length) return;
    setAssets((prev) => ({ ...clipboard.assets, ...prev }));
    addItems(cloneItems(clipboard.items));
  }, [addItems, cloneItems]);

  const align = useCallback(
    (mode) => setItems(alignItems(docRef.current.items, selectedRef.current, mode, aspectRatio(docRef.current.aspect))),
    [setItems]
  );

  const distribute = useCallback(
    (axis) =>
      setItems(distributeItems(docRef.current.items, selectedRef.current, axis, aspectRatio(docRef.current.aspect))),
    [setItems]
  );

  const runAutoArrange = useCallback(() => {
    const next = autoArrange(docRef.current.items, docRef.current.aspect, assetsRef.current);
    if (!next) {
      toast.info("Add at least two images to arrange.", { toastId: "arrange" });
      return;
    }
    setItems(next);
  }, [setItems]);

  const imageKeys = useCallback(
    () =>
      [...new Set(syncPage(docRef.current).pages.flatMap((page) => page.items).filter((item) => item.type === "image" && item.source).map((item) => `${item.source}:${item.ref}`))],
    []
  );

  const extractPalette = useCallback(async () => {
    const refs = imageKeys();
    if (!refs.length) {
      toast.info("Add some images first.", { toastId: "palette" });
      return;
    }
    setPaletteBusy(true);
    try {
      const { data } = await client.post("/api/moodboards/palette", { refs, count: 5 });
      setPalette(data.colors || []);
      if (!data.colors?.length) toast.info("No colours found in these images.");
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not read colours from the images.");
    } finally {
      setPaletteBusy(false);
    }
  }, [imageKeys]);

  const addPaletteSwatches = useCallback(() => {
    if (!palette.length) return;
    const ratio = aspectRatio(docRef.current.aspect);
    const size = 7;
    const gap = 1;
    const height = size * ratio;
    const startX = 100 - 3 - palette.length * size - (palette.length - 1) * gap;
    const y = 100 - 3 * ratio - height;
    addItems(
      palette.map((color, index) =>
        makeSwatch({ x: Math.max(0, startX + index * (size + gap)), y: Math.max(0, y), w: size, h: height }, { color })
      )
    );
  }, [addItems, palette]);

  const removeItemBackground = useCallback(
    async (item) => {
      setRemovingItem(item.id);
      const asset = await uploads.removeBackground(item.ref);
      setRemovingItem(null);
      if (!asset) return;
      setAssets((prev) => ({ ...prev, [`${asset.source}:${asset.ref}`]: asset }));
      // The cut-out is trimmed to the subject, so an old crop no longer lines up.
      updateItem(item.id, { ref: asset.ref, fit: "contain", focus_x: 50, focus_y: 50, zoom: 1, crop: null });
    },
    [updateItem, uploads]
  );

  const boardColours = useMemo(() => {
    if (!doc) return [];
    const colours = [doc.background];
    doc.items.forEach((item) => {
      if (item.type === "swatch" || item.type === "text") colours.push(item.color);
      if (item.stroke) colours.push(item.stroke);
      if (item.fill) colours.push(item.fill);
    });
    return [...new Set(colours.filter(Boolean))].slice(0, 12);
  }, [doc]);

  // --- keyboard -------------------------------------------------------------

  useEffect(() => {
    const onKey = (event) => {
      if (!docRef.current || dialog || cropId || isTyping(event.target)) return;
      const mod = event.ctrlKey || event.metaKey;
      const key = event.key.toLowerCase();

      if (mod && key === "z") {
        event.preventDefault();
        event.shiftKey ? redo() : undo();
      } else if (mod && key === "y") {
        event.preventDefault();
        redo();
      } else if (mod && key === "a") {
        event.preventDefault();
        setSelectedIds(docRef.current.items.map((item) => item.id));
      } else if (mod && key === "c") {
        copySelected();
      } else if (mod && key === "v") {
        event.preventDefault();
        paste();
      } else if (mod && key === "d") {
        event.preventDefault();
        duplicateSelected();
      } else if (!mod && (key === "pageup" || key === "pagedown")) {
        event.preventDefault();
        goToPage(docRef.current.page + (key === "pagedown" ? 1 : -1));
      } else if (key === "escape") {
        setTool((current) => {
          if (current === "select") setSelectedIds([]);
          return "select";
        });
      } else if (!mod && !event.altKey && TOOLS.some((entry) => entry.shortcut === key)) {
        setTool(TOOLS.find((entry) => entry.shortcut === key).key);
      } else if (!selectedRef.current.length) {
        // Everything below needs a selection.
      } else if (key === "delete" || key === "backspace") {
        event.preventDefault();
        removeSelected();
      } else if (key.startsWith("arrow")) {
        event.preventDefault();
        const step = event.shiftKey ? 2 : 0.5;
        const ratio = aspectRatio(docRef.current.aspect);
        const dx = key === "arrowleft" ? -step : key === "arrowright" ? step : 0;
        const dy = key === "arrowup" ? -step * ratio : key === "arrowdown" ? step * ratio : 0;
        setItems(
          docRef.current.items.map((item) =>
            selectedRef.current.includes(item.id) && !item.locked
              ? { ...item, x: clamp(item.x + dx, 0, 100 - item.w), y: clamp(item.y + dy, 0, 100 - item.h) }
              : item
          ),
          { merge: "nudge" }
        );
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [copySelected, cropId, dialog, duplicateSelected, goToPage, paste, redo, removeSelected, setItems, undo]);

  // A crop asked for an image that is gone or not loaded yet would otherwise
  // leave the shortcuts switched off with no dialog to close.
  useEffect(() => {
    if (!cropId) return;
    const item = doc?.items.find((entry) => entry.id === cropId);
    if (!item?.source || !assets[`${item.source}:${item.ref}`]?.url) setCropId(null);
  }, [assets, cropId, doc]);

  // --- export ---------------------------------------------------------------

  const exportBoard = async (format) => {
    setExporting(format);
    try {
      await save();
      if (statusRef.current === "conflict" || statusRef.current === "error") {
        toast.error("Save the board before exporting.");
        return;
      }
      const response = await client.get(`/api/moodboards/${boardId}/export`, {
        params: format === "png" ? { format, page: docRef.current.page + 1 } : { format },
        responseType: "blob",
        timeout: 180000,
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileNameFrom(response.headers["content-disposition"], `moodboard.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error("Export failed:", err);
      toast.error("Could not export the board.");
    } finally {
      setExporting(null);
    }
  };

  // --- render ---------------------------------------------------------------

  if (loadError) {
    return (
      <main className="mb-page">
        <p className="mb-error">{loadError}</p>
        <Link to="/moodboards" className="mb-text-button">
          Back to boards
        </Link>
      </main>
    );
  }

  if (!doc) {
    return (
      <main className="mb-page">
        <p className="mb-muted">Loading board...</p>
      </main>
    );
  }

  const selected = doc.items.filter((item) => selectedIds.includes(item.id));
  const ratio = aspectRatio(doc.aspect);
  const cropCandidate = cropId && doc.items.find((item) => item.id === cropId);
  const cropItem = cropCandidate?.source && assets[`${cropCandidate.source}:${cropCandidate.ref}`]?.url ? cropCandidate : null;

  return (
    <main className="mb-editor">
      <ToastContainer position="bottom-right" />

      <header className="mb-toolbar">
        <div className="mb-toolbar-start">
          <Link to="/moodboards" className="mb-icon-button" aria-label="Back to boards" title="Back to boards">
            <ArrowLeft size={18} strokeWidth={1.8} />
          </Link>
          <input
            className="mb-title-input"
            value={doc.title}
            maxLength={120}
            onChange={(event) => updateBoard({ title: event.target.value }, { record: false })}
            onBlur={() => !doc.title.trim() && updateBoard({ title: "Untitled board" }, { record: false })}
            aria-label="Board title"
          />
          <span className={`mb-status is-${status}`} role="status">
            {STATUS_LABELS[status]}
            {status === "error" && (
              <button type="button" className="mb-text-button" onClick={save}>
                Retry
              </button>
            )}
          </span>
        </div>

        <div className="mb-toolbar-group">
          <button type="button" className="mb-icon-button" onClick={undo} disabled={!historyFlags.undo} title="Undo (Ctrl+Z)" aria-label="Undo">
            <Undo2 size={17} strokeWidth={1.8} />
          </button>
          <button type="button" className="mb-icon-button" onClick={redo} disabled={!historyFlags.redo} title="Redo (Ctrl+Shift+Z)" aria-label="Redo">
            <Redo2 size={17} strokeWidth={1.8} />
          </button>
          <span className="mb-divider" aria-hidden="true" />
          <div className="mb-toolset" role="radiogroup" aria-label="Tool">
            {TOOLS.map(({ key, icon: Icon, label, shortcut }) => (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={tool === key}
                aria-label={label}
                title={`${label} (${shortcut.toUpperCase()})`}
                className={tool === key ? "active" : ""}
                onClick={() => setTool(key)}
              >
                <Icon size={16} strokeWidth={1.8} />
              </button>
            ))}
          </div>
          <span className="mb-divider" aria-hidden="true" />
          <button type="button" className="mb-tool" onClick={() => addItem(makeImage(centred(24, (24 * ratio) / 1.5)))}>
            <ImagePlus size={16} strokeWidth={1.8} aria-hidden="true" />
            Frame
          </button>
          <button type="button" className="mb-tool" onClick={() => addItem(makeText(centred(34, 8 * ratio)))}>
            <Type size={16} strokeWidth={1.8} aria-hidden="true" />
            Text
          </button>
          <button type="button" className="mb-tool" onClick={() => addItem(makeSwatch(centred(10, 10 * ratio)))}>
            <Palette size={16} strokeWidth={1.8} aria-hidden="true" />
            Swatch
          </button>
          <button type="button" className="mb-tool" onClick={runAutoArrange} title="Re-flow all images into neat rows">
            <LayoutGrid size={16} strokeWidth={1.8} aria-hidden="true" />
            Arrange
          </button>
        </div>

        <div className="mb-toolbar-group">
          <button type="button" className="mb-tool" onClick={() => setDialog("share")}>
            <Share2 size={16} strokeWidth={1.8} aria-hidden="true" />
            {shareToken ? "Shared" : "Share"}
          </button>
          <button type="button" className="mb-tool" onClick={() => exportBoard("png")} disabled={Boolean(exporting)}>
            <Download size={16} strokeWidth={1.8} aria-hidden="true" />
            {exporting === "png" ? "Preparing..." : "PNG"}
          </button>
          <button type="button" className="mb-primary" onClick={() => exportBoard("pdf")} disabled={Boolean(exporting)}>
            <Download size={16} strokeWidth={1.8} aria-hidden="true" />
            {exporting === "pdf" ? "Preparing..." : "PDF"}
          </button>
        </div>
      </header>

      {tool !== "select" && (
        <DrawOptions kind={tool} layout="bar" value={toolStyles[tool]} onChange={(patch) => changeToolStyle(tool, patch)} />
      )}

      {status === "conflict" && (
        <div className="mb-banner" role="alert">
          <span>This board was changed in another tab or window.</span>
          <button type="button" className="mb-text-button" onClick={() => resolveConflict(false)}>
            Load latest
          </button>
          <button type="button" className="mb-primary" onClick={() => resolveConflict(true)}>
            Keep my version
          </button>
        </div>
      )}

      <AssetPanel uploads={uploads} onPick={placeAsset} />

      <section className="mb-canvas-area" aria-label="Board">
        <BoardCanvas
          board={doc}
          assets={assets}
          editable
          fit
          selectedIds={selectedIds}
          onSelect={setSelectedIds}
          onChange={setItems}
          onDropAsset={placeAsset}
          onDropFiles={dropFiles}
          tool={tool}
          toolStyle={toolStyles[tool] || {}}
          onCreate={createDrawn}
          onOpenCrop={setCropId}
        />
        <PageStrip
          pages={doc.pages}
          page={doc.page}
          aspect={doc.aspect}
          assets={assets}
          onSelect={goToPage}
          onAdd={addPage}
          onDuplicate={duplicatePage}
          onDelete={deletePage}
          onMove={movePage}
        />
      </section>

      <Inspector
        doc={doc}
        items={selected}
        assets={assets}
        boardColours={boardColours}
        palette={palette}
        paletteBusy={paletteBusy}
        bgAvailable={uploads.backgroundRemoval}
        removingBackground={Boolean(removingItem)}
        onBoardChange={updateBoard}
        onItemChange={changeFromInspector}
        onItemsChange={updateItems}
        onChangeLayout={() => setDialog("layout")}
        onAutoArrange={runAutoArrange}
        onExtractPalette={extractPalette}
        onAddSwatches={addPaletteSwatches}
        onArrange={arrange}
        onAlign={align}
        onDistribute={distribute}
        onDuplicate={duplicateSelected}
        onDelete={removeSelected}
        onRemoveBackground={removeItemBackground}
        onOpenCrop={setCropId}
      />

      {cropItem && (
        <CropDialog
          key={cropItem.id}
          item={cropItem}
          asset={assets[`${cropItem.source}:${cropItem.ref}`]}
          ratio={ratio}
          onClose={() => setCropId(null)}
          onApply={(patch) => {
            updateItem(cropItem.id, patch);
            setCropId(null);
          }}
        />
      )}
      {dialog === "share" && (
        <ShareDialog boardId={boardId} token={shareToken} onToken={setShareToken} onClose={() => setDialog(null)} />
      )}
      {dialog === "layout" && (
        <LayoutDialog
          doc={doc}
          onClose={() => setDialog(null)}
          onApply={(items) => {
            setItems(items);
            setSelectedIds([]);
            setDialog(null);
          }}
        />
      )}
    </main>
  );
}
